import { GEM_NAMES, GEM_SHAPES, GLYPHS, PALETTE, STATUS_BY_ID, STATUS_IDS } from "@eldrun/content";
import { Vector3, type Camera } from "three";
import { gameColor } from "../../render/style/roles.ts";
import { h } from "../kit/dom.ts";

/** Os glifos dos status de uma máscara (ordem de `STATUS_IDS`). */
export function statusGlyphs(mask: number): string {
  return statusList(mask).map((s) => s.glyph).join("");
}

/**
 * A forma da gema sobre o Guardião (F76, doc 09 §2.4: a gema se lê pela forma, não pela cor), na cor do chefe;
 * `undefined` para quem não é Guardião.
 */
export function guardianMark(type: string): { glyph: string; name: string; color: string } | undefined {
  const color = type.startsWith("guardiao-") ? (type.slice("guardiao-".length) as keyof typeof GEM_SHAPES) : undefined;
  const shape = color ? GEM_SHAPES[color] : undefined;
  if (!color || !shape) return undefined;
  return { glyph: GLYPHS[shape].char, name: GEM_NAMES[color], color: gameColor(PALETTE.chefe.hex) };
}

export interface StatusChip {
  glyph: string;
  name: string;
  /** Acaba em menos de `STATUS_ENDING_MS` (F76): pisca 3×. */
  ending: boolean;
}

/** Os status de uma máscara, com os que estão acabando (`ending`, a outra máscara). */
export function statusList(mask: number, ending = 0): StatusChip[] {
  const out: StatusChip[] = [];
  STATUS_IDS.forEach((id, i) => {
    if (mask & (1 << i)) out.push({ glyph: STATUS_BY_ID.get(id)!.glyph, name: STATUS_BY_ID.get(id)!.name, ending: (ending & (1 << i)) !== 0 });
  });
  return out;
}

/**
 * Escreve os glifos em `el` (um `span` por status; `.acaba` pisca), só quando mudam. `mark`: um glifo fixo
 * antes deles (F76: a forma da gema do Guardião).
 */
export function renderStatusChips(el: HTMLElement, chips: readonly StatusChip[], mark?: { glyph: string; name: string }): void {
  const key = `${mark?.glyph ?? ""}|${chips.map((c) => `${c.glyph}${c.ending ? "!" : ""}`).join("")}`;
  if (el.dataset["chips"] === key) return;
  el.dataset["chips"] = key;
  el.replaceChildren(
    ...(mark ? [h("span", { class: "marca", title: mark.name }, mark.glyph)] : []),
    ...chips.map((c) => h("span", { class: c.ending ? "glifo acaba" : "glifo", title: c.name }, c.glyph)),
  );
}

/** Até quantas criaturas mostram glifos de uma vez, e até que distância (m). */
const POOL = 16;
const RANGE = 45;

/**
 * Glifos dos status sobre as criaturas (F65): ⛓ Preso, ≈ Lento, ⌖ Marcado, ! Provocado, ◎ Exposto, na
 * cor de quem aplicou (a regra "cor = fonte"). DOM projetado, um conjunto fixo de elementos.
 */
export class StatusGlyphs {
  readonly element: HTMLElement;
  private readonly items: HTMLElement[] = [];
  private readonly p = new Vector3();

  constructor(parent: HTMLElement) {
    const root = (this.element = h("div", { id: "status-criaturas", "aria-hidden": "true" }));
    for (let i = 0; i < POOL; i++) {
      const el = h("span", { class: "status" });
      el.hidden = true;
      this.items.push(el);
      root.appendChild(el);
    }
    parent.appendChild(root);
  }

  update(
    creatures: Iterable<{ x: number; y: number; z: number; status: number; ending?: number; color: string; height: number; mark?: { glyph: string; name: string; color: string } }>,
    camera: Camera,
    width: number,
    height: number,
  ): void {
    let k = 0;
    for (const c of creatures) {
      if ((c.status === 0 && !c.mark) || k >= POOL) continue;
      if (camera.position.distanceTo(this.p.set(c.x, c.y, c.z)) > RANGE) continue;
      this.p.set(c.x, c.y + c.height + 0.5, c.z).project(camera);
      if (this.p.z > 1 || this.p.z < -1 || Math.abs(this.p.x) > 1.05 || Math.abs(this.p.y) > 1.05) continue;
      const el = this.items[k++]!;
      el.hidden = false;
      renderStatusChips(el, statusList(c.status, c.ending ?? 0), c.mark);
      if (c.mark) el.style.setProperty("--marca", c.mark.color);
      el.style.setProperty("--cor", c.color);
      el.style.transform = `translate(${(((this.p.x + 1) / 2) * width).toFixed(0)}px, ${(((1 - this.p.y) / 2) * height).toFixed(0)}px) translate(-50%, -100%)`;
    }
    for (; k < POOL; k++) this.items[k]!.hidden = true;
  }
}
